import type { User, UserRole } from '@rendimees24/shared-types';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

/** Internal-staff roles that the Sisemised filter narrows to. */
export const INTERNAL_ROLES: UserRole[] = ['admin', 'dispatcher', 'finance'];

/**
 * Subscribe to every /users doc — admins read all roles. The Kasutajad page
 * filters client-side; this gives one stream that covers every tab and the
 * counts on the role chips without round-trips per filter.
 *
 * No `orderBy` on the server query: Firestore treats orderBy as a predicate
 * and silently drops docs that lack the field, so legacy or hand-edited
 * /users docs without createdAt would disappear from the list. We sort by
 * createdAt client-side and tolerate missing values.
 */
export function subscribeAllUsers(onSnap: (users: User[]) => void): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(collection(firestore, 'users'), (snap: QuerySnapshot) => {
    const users = snap.docs.map(
      (d) => ({ id: d.id, ...(d.data() as DocumentData) }) as User,
    );
    users.sort((a, b) => {
      const at = typeof a.createdAt === 'string' ? Date.parse(a.createdAt) : 0;
      const bt = typeof b.createdAt === 'string' ? Date.parse(b.createdAt) : 0;
      return bt - at;
    });
    onSnap(users);
  });
}

/**
 * Stamp /users/{uid}.lastSignInAt = now if the previous value is older
 * than `freshnessMs` (default 1 hour). Called from AuthContext on app
 * load — rate-limiting prevents burning a write on every page refresh
 * while still keeping the field fresh enough for the Users page list.
 */
export async function stampLastSignInIfStale(
  uid: string,
  previousIso: string | null | undefined,
  freshnessMs = 60 * 60 * 1000,
): Promise<void> {
  if (previousIso) {
    const prev = new Date(previousIso).getTime();
    if (!Number.isNaN(prev) && Date.now() - prev < freshnessMs) {
      return; // still fresh, skip the write
    }
  }
  const { firestore } = getFirebase();
  await setDoc(
    doc(firestore, 'users', uid),
    { lastSignInAt: serverTimestamp() },
    { merge: true },
  );
}

/* ─── Admin actions on /users docs ──────────────────────────────────── */

export interface UpdateUserInput {
  uid: string;
  role?: UserRole;
  status?: 'active' | 'suspended' | 'pending';
  statusReason?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export async function updateUser(input: UpdateUserInput): Promise<void> {
  const { functions } = getFirebase();
  const callable = httpsCallable<UpdateUserInput, { ok: true }>(functions, 'updateUser');
  await callable(input);
}

export interface ResendInviteResult {
  resetLink: string;
}

export async function resendInvite(uid: string): Promise<ResendInviteResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ uid: string }, ResendInviteResult>(
    functions,
    'resendInvite',
  );
  const { data } = await callable({ uid });
  return data;
}
